import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import OndemandVideoRoundedIcon from '@mui/icons-material/OndemandVideoRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import StyleRoundedIcon from '@mui/icons-material/StyleRounded';
import {
  PRACTICE_SEGMENT,
  buildExampleVideosPath,
  buildPracticeAllMaterialPath,
  buildPracticePath,
  buildPracticeTopicPath,
} from '../../utils/contentRoutes';
import { readLastPracticeView, readLastTopicId } from '../../utils/practiceContext';
import { SECTION_VOCAB_TOPIC_LIST, TOPIC_CATALOG } from './topicCatalog';

/**
 * The four things a practice topic can be studied through, plus the FR-only video
 * library. This replaced the old "Other Materials" view: the same destinations, but
 * chosen beside the topic list rather than on a page of their own, so the circles
 * update as you switch.
 *
 * Both the map's topic catalog (`/{lang}/fide-test-topics`) and the practice sidebar render from here,
 * so the two can never drift apart.
 */
export const PRACTICE_MATERIALS = ['dialogue', 'discussion', 'vocab', 'flashcards'];

/** Vocabulary and flashcards are open to everyone; the two speaking tracks are tier-gated. */
export const isGatedMaterial = (material) => material === 'dialogue' || material === 'discussion';

export const normalizeMaterial = (material) => (PRACTICE_MATERIALS.includes(material) ? material : null);

/**
 * Which material to open with when the URL doesn't say: what the learner was last
 * reading, else dialogue.
 */
export const resolveMaterial = (requested) => normalizeMaterial(requested) ?? readLastPracticeView() ?? 'dialogue';

const MATERIAL_META = {
  dialogue: { label: 'Dialogue', shortLabel: 'Dialogue', Icon: QuestionAnswerRoundedIcon },
  // `badge` shows only while the material is the selected one: picking Discussion is
  // the moment to say which exam level it is written for.
  discussion: { label: 'Discussion', shortLabel: 'Discussion', badge: 'B1 section', Icon: ForumRoundedIcon },
  vocab: { label: 'Vocabulary', shortLabel: 'Vocab', Icon: MenuBookRoundedIcon },
  flashcards: { label: 'Flashcards', shortLabel: 'Cards', Icon: StyleRoundedIcon },
};

/**
 * The buttons of the 2×2 grid, plus the videos row.
 *
 * Videos is not a material — it is one global page with its own filters, and it only
 * exists under `/fr/`. It rides along as a fifth, full-width button so learners can
 * reach it from the same place, and is followed in place like any other link.
 */
export const materialOptions = () => PRACTICE_MATERIALS.map((id) => ({ id, ...MATERIAL_META[id] }));

export const videoOption = (lang) =>
  lang === 'fr'
    ? {
        id: 'videos',
        label: 'Extra Videos',
        shortLabel: 'Extra Videos',
        Icon: OndemandVideoRoundedIcon,
        to: buildExampleVideosPath(),
      }
    : null;

/**
 * The topics a material actually has.
 *
 * Dialogue stops at topic 11 and discussion runs to 16, so switching materials adds
 * and removes circles. Vocabulary and flashcards cover every catalog topic *and* the
 * three A2 section word sets (Introduction / Situation description / Telephone),
 * which have no speaking track of their own.
 */
export const OTHER_VOCAB_TOPIC = {
  id: 'other',
  name: { fr: 'Other', de: 'Other', en: 'Other' },
  Icon: StyleRoundedIcon,
};

export const topicsForMaterial = (material, { includeOther = false } = {}) => {
  if (material === 'dialogue') return TOPIC_CATALOG.filter((topic) => topic.hasDialogue);
  if (material === 'discussion') return TOPIC_CATALOG.filter((topic) => topic.hasDiscussion);
  return [...TOPIC_CATALOG, ...SECTION_VOCAB_TOPIC_LIST, ...(includeOther ? [OTHER_VOCAB_TOPIC] : [])];
};

/**
 * Where a topic leads for the material being browsed.
 *
 * Every topic — the A2 section word sets included — uses the Topic catalog's own
 * URL shape here. They have guided-section URLs too (`/fide-practice/a2/image/vocab`,
 * reached from the level rail's tabs), but landing there from the catalog dropped
 * the learner into the Level rail mid-browse. The page renders from the topic id
 * either way, so the shape is free to say which rail you came in on.
 */
export const getTopicMaterialPath = (lang, topic, material) => buildPracticeTopicPath(lang, topic.id, material);

/**
 * Opens a material on the last catalog topic where that material exists, or its
 * first available topic when the learner has not opened a compatible topic yet.
 */
export const getRememberedTopicMaterialPath = (lang, material) => {
  const topics = topicsForMaterial(material);
  const topic = topics.find((entry) => String(entry.id) === readLastTopicId()) ?? topics[0];
  return topic ? getTopicMaterialPath(lang, topic, material) : buildPracticePath(lang);
};

const GUIDED_VOCAB_PATTERN = new RegExp(`/${PRACTICE_SEGMENT}/a2/([a-z0-9-]+)/(?:vocab|flashcards)/?$`);

/**
 * The word-set id a guided section's vocab/flashcards URL is showing
 * (`/fide-practice/a2/intro/vocab` → 17), so the rail can highlight that row. Catalog
 * topics carry their id in the URL; these carry the section slug instead.
 */
export const getGuidedVocabTopicId = (pathname = '') => {
  const slug = pathname.match(GUIDED_VOCAB_PATTERN)?.[1];
  return SECTION_VOCAB_TOPIC_LIST.find((topic) => topic.sectionSlug === slug)?.id ?? null;
};

/** The full-library page behind a material, or null where there isn't one. */
export const materialLibraryPath = (lang, material) => {
  if (material === 'vocab') return buildPracticeAllMaterialPath(lang, 'vocab');
  if (material === 'flashcards') return buildPracticeAllMaterialPath(lang, 'flashcards');
  return null;
};
